import { useQuery } from "@tanstack/react-query";
import { api, buildUrl, type RegionResponse, type LocationResponse, type CampingResponse } from "@shared/routes";
import { useAppStore } from "@/store/use-app-store";
import type { ItineraryWithItems, EditorialGuideRow, ItineraryBlogRow, Festival } from "@shared/schema";

// --- REGIONS ---
export function useRegions(opts?: { enabled?: boolean }) {
  const enabled = opts?.enabled !== false;
  return useQuery({
    queryKey: [api.regions.list.path],
    queryFn: async () => {
      const res = await fetch(api.regions.list.path, { credentials: "include" });
      if (!res.ok) throw new Error("Failed to fetch regions");
      return api.regions.list.responses[200].parse(await res.json());
    },
    enabled,
    staleTime: 5 * 60 * 1000,
  });
}

export function useRegion(slug: string) {
  return useQuery({
    queryKey: [api.regions.getBySlug.path, slug],
    queryFn: async () => {
      const url = buildUrl(api.regions.getBySlug.path, { slug });
      const res = await fetch(url, { credentials: "include" });
      if (res.status === 404) return null;
      if (!res.ok) throw new Error("Failed to fetch region");
      return api.regions.getBySlug.responses[200].parse(await res.json());
    },
    enabled: !!slug,
  });
}

// --- LOCATIONS ---
export function useLocations(params?: { regionId?: number; category?: string; enabled?: boolean }) {
  const enabled = params?.enabled !== false;
  return useQuery({
    queryKey: [api.locations.list.path, { regionId: params?.regionId, category: params?.category }],
    queryFn: async () => {
      const urlParams = new URLSearchParams();
      if (params?.regionId) urlParams.append("regionId", params.regionId.toString());
      if (params?.category) urlParams.append("category", params.category);
      
      const url = `${api.locations.list.path}${urlParams.toString() ? `?${urlParams.toString()}` : ''}`;
      
      const res = await fetch(url, { credentials: "include" });
      if (!res.ok) throw new Error("Failed to fetch locations");
      return api.locations.list.responses[200].parse(await res.json());
    },
    enabled,
    staleTime: 5 * 60 * 1000,
  });
}

export function useLocation(slug: string) {
  return useQuery({
    queryKey: [api.locations.getBySlug.path, slug],
    queryFn: async () => {
      const url = buildUrl(api.locations.getBySlug.path, { slug });
      const res = await fetch(url, { credentials: "include" });
      if (res.status === 404) return null;
      if (!res.ok) throw new Error("Failed to fetch location");
      return api.locations.getBySlug.responses[200].parse(await res.json());
    },
    enabled: !!slug,
  });
}

export function useCompareLocations(slugs: string[]) {
  return useQuery({
    queryKey: [api.locations.compare.path, slugs.join(',')],
    queryFn: async () => {
      if (slugs.length === 0) return [];
      const url = `${api.locations.compare.path}?slugs=${encodeURIComponent(slugs.join(','))}`;
      const res = await fetch(url, { credentials: "include" });
      if (!res.ok) throw new Error("Failed to fetch comparison");
      return api.locations.compare.responses[200].parse(await res.json());
    },
    enabled: slugs.length > 0,
  });
}

// --- HIDDEN GEMS ---
export function useHiddenGems() {
  return useQuery({
    queryKey: ["/api/locations/hidden-gems"],
    queryFn: async () => {
      const res = await fetch("/api/locations/hidden-gems", { credentials: "include" });
      if (!res.ok) throw new Error("Failed to fetch hidden gems");
      return res.json() as Promise<LocationResponse[]>;
    },
  });
}

// --- NATIONAL PARKS ---
export function useNationalParks(opts?: { enabled?: boolean }) {
  const enabled = opts?.enabled !== false;
  return useQuery({
    queryKey: [api.nationalParks.list.path],
    queryFn: async () => {
      const res = await fetch(api.nationalParks.list.path, { credentials: "include" });
      if (!res.ok) throw new Error("Failed to fetch national parks");
      return api.nationalParks.list.responses[200].parse(await res.json());
    },
    enabled,
    staleTime: 5 * 60 * 1000,
  });
}

export function useNationalPark(slug: string) {
  return useQuery({
    queryKey: [api.nationalParks.getBySlug.path, slug],
    queryFn: async () => {
      const url = buildUrl(api.nationalParks.getBySlug.path, { slug });
      const res = await fetch(url, { credentials: "include" });
      if (res.status === 404) return null;
      if (!res.ok) throw new Error("Failed to fetch national park");
      return api.nationalParks.getBySlug.responses[200].parse(await res.json());
    },
    enabled: !!slug,
  });
}

// --- CAMPINGS ---
export function useCampings(regionId?: number) {
  return useQuery({
    queryKey: [api.campings.list.path, regionId],
    queryFn: async () => {
      const urlParams = new URLSearchParams();
      if (regionId) urlParams.append("regionId", regionId.toString());
      
      const url = `${api.campings.list.path}${urlParams.toString() ? `?${urlParams.toString()}` : ''}`;
      const res = await fetch(url, { credentials: "include" });
      if (!res.ok) throw new Error("Failed to fetch campings");
      return api.campings.list.responses[200].parse(await res.json());
    },
  });
}

// --- ITINERARY ---
export function useItinerary() {
  const { user } = useAppStore();
  return useQuery({
    queryKey: ["/api/itinerary", user?.id],
    queryFn: async () => {
      if (!user) return null;
      const res = await fetch("/api/itinerary", { credentials: "include" });
      if (!res.ok) return null;
      return res.json() as Promise<ItineraryWithItems>;
    },
    enabled: !!user,
  });
}

// --- FAVORITES ---
export function useFavorites() {
  const { user } = useAppStore();
  return useQuery({
    queryKey: ["/api/favorites", user?.id],
    queryFn: async () => {
      if (!user) return [];
      const res = await fetch("/api/favorites", { credentials: "include" });
      if (!res.ok) return [];
      return res.json() as Promise<LocationResponse[]>;
    },
    enabled: !!user,
  });
}

// --- FESTIVALS ---
export function useFestivals(opts?: { year?: number; enabled?: boolean }) {
  const enabled = opts?.enabled !== false;
  const year = opts?.year;
  return useQuery({
    queryKey: ["/api/festivals", year ?? "current"],
    queryFn: async () => {
      const url = year ? `/api/festivals?year=${year}` : "/api/festivals";
      const res = await fetch(url, { credentials: "include" });
      if (!res.ok) throw new Error("Failed to fetch festivals");
      return res.json() as Promise<Festival[]>;
    },
    enabled,
    staleTime: 5 * 60 * 1000,
  });
}

// --- ITINERARY BLOGS ---
export function useItineraryBlogs() {
  return useQuery({
    queryKey: ["/api/itinerary-blogs"],
    queryFn: async () => {
      const res = await fetch("/api/itinerary-blogs", { credentials: "include" });
      if (!res.ok) throw new Error("Failed to fetch itinerary blogs");
      return res.json() as Promise<ItineraryBlogRow[]>;
    },
    staleTime: 5 * 60 * 1000,
  });
}

export function useItineraryBlog(slug: string) {
  return useQuery({
    queryKey: ["/api/itinerary-blogs", slug],
    queryFn: async () => {
      const res = await fetch(`/api/itinerary-blogs/${encodeURIComponent(slug)}`, { credentials: "include" });
      if (res.status === 404) return null;
      if (!res.ok) throw new Error("Failed to fetch itinerary blog");
      return res.json() as Promise<ItineraryBlogRow>;
    },
    enabled: !!slug,
    staleTime: 5 * 60 * 1000,
  });
}

// --- EDITORIAL GUIDES ---
export function useGuides() {
  return useQuery({
    queryKey: ["/api/editorial-guides"],
    queryFn: async () => {
      const res = await fetch("/api/editorial-guides", { credentials: "include" });
      if (!res.ok) throw new Error("Failed to fetch editorial guides");
      return res.json() as Promise<EditorialGuideRow[]>;
    },
    staleTime: 5 * 60 * 1000,
  });
}

export function useGuide(slug: string) {
  return useQuery({
    queryKey: ["/api/editorial-guides", slug],
    queryFn: async () => {
      const res = await fetch(`/api/editorial-guides/${encodeURIComponent(slug)}`, { credentials: "include" });
      if (res.status === 404) return null;
      if (!res.ok) throw new Error("Failed to fetch editorial guide");
      return res.json() as Promise<EditorialGuideRow>;
    },
    enabled: !!slug,
    staleTime: 5 * 60 * 1000,
  });
}
